/* tslint:disable:import-spacing no-inferrable-types variable-name align no-trailing-whitespace */

import {MovementHistory} from '@/models/scenario/movement.history';

import {ViewMemento}     from './view.memento';
import {ViewerInterface} from './viewer.interface';

import * as $            from 'jquery';

/**
 *
 * @class SelectionManager
 *
 * @brief tracks and manages the currently selected train histories.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      November 2019
 *
 */
export class SelectionManager {

  /** @brief _viewerViewerInterface the main viewer of the graph */
  private     _viewer: ViewerInterface = null;

  /** @brief _selectionMovementHistory[] the current selection group */
  private  _selection: {
       view: ViewMemento,
    history: MovementHistory,
      index: number
  }[] = [];

  /* drag resize support */

  private      _startX: number         = 0;
  private      _startY: number         = 0;
  private  _startWidth: number         = 0;
  private _startHeight: number         = 0;
  private    _dragging: boolean        = false;

  /** @var _focusIndex number pointer to the item we're focused on */
  private _focusIndex: number          = -1;

  /** @var _pillContainer JQuery the panel where we show the selection pillbox buttons */
  private _pillContainer: JQuery       = null;

  /** @var _nightMode boolean are we in printing mode or view mode? */
  private _nightMode: boolean          = true;

  /* the viewer windows have to have uniqu'ified DOM since there may be more othan one viewer in the browser */
  private uuid: number                 = 0;

  /** @brief standard constructor */
  constructor(viewer: ViewerInterface, uuid: number) {

    this._viewer = viewer;
    this.uuid    = uuid;
  }

  /** @brief switch between print and viewing mode */
  nightMode(mode: boolean): void {

    this._nightMode = mode;

    this.updatePanel();
  }

  /** @brief enter resize-drag mode */
  startDrag(e: JQuery, event: MouseEvent): void {

    if (this._dragging) {

      /* already started dragging */

      return;
    }

    this._dragging    = true;

    this._startX      = event.clientX;
    this._startY      = event.clientY;
    this._startWidth  = $(e).width();
    this._startHeight = $(e).height();

  }

  /** @brief drag the panel size out */
  drag(e: JQuery, event: MouseEvent): void {

    if (!this._dragging) {

      /* not in drag mode */

      return;
    }

    const  newWidth: number  = this._startWidth  + this._startX - event.clientX;
    const newHeight: number = this._startHeight + event.clientY - this._startY;

    $(e).width(newWidth);
    $(e).height(newHeight);

  }

  /** @brief stop resizing the panel */
  stopDrag(e: JQuery, event: MouseEvent): void {

    if (!this._dragging) {

      /* not in drag mode */

      return;
    }

    this._dragging    = false;
  }

    /** @brief given the #id of the action button and its dropdown, setup the resizable dropdown */
  finalize(idOfActionButton: string, idOfDropdown: string) {

    const           e: JQuery = $('#' + idOfDropdown);

    this._pillContainer = e;

    /* setup the resize behavior for the drop down panel */

    $(e).append('<div class="resizer-left"></div>');
    $(e).append('<div class="resizer-bottom"></div>');
    $(e).append('<div class="resizer-both-left"></div>');

    const startFN = (event) => {

      this.startDrag(e, event);

    };

    $( '#' + idOfDropdown + ' .resizer-left' ).on('mousedown', startFN);
    $( '#' + idOfDropdown + ' .resizer-bottom' ).on('mousedown', startFN);
    $( '#' + idOfDropdown + ' .resizer-both-left' ).on('mousedown', startFN);

    const dragFN = (event) => {

      this.drag(e, event);

    };

    document.documentElement.addEventListener('mousemove', dragFN, false);

    const stopFN = (event) => {

      this.stopDrag(e, event);

    };

    document.documentElement.addEventListener('mouseup', stopFN, false);

  }

  /** @brief render the selection pillbox buttons per the current selection group */
  updatePanel(): void {

    /* remove the old selection buttons */

    $(this._pillContainer).find('.select-pill-scroller').remove();

    let html: string           = '';
    html                      += '<span class="select-pill-scroller">';

    $(this._pillContainer).find('.select-pill-wrapper.focused').removeClass('focused');

    for (const item of this._selection) {

      const   moves: MovementHistory = item.history;
      const    text: string          = moves._train.generatedName();
      const      id: number          = moves._train._generatedId;
      const classes: string          = (item.index === this._focusIndex) ? 'focused' : '';
      let      attr: string          = '';
      attr                          += ' data-id="' + id + '"';

      html                          += '  <span class="select-pill-wrapper ' + classes + '" ' + attr + '>';
      html                          += '    <span class="action">' + text + '-' + id + '</span>';
      html                          += '    <span class="button"><i class="fa fa-close"></i></span>';
      html                          += '  </span>';
    }

    html                            += '</span>';

    $(this._pillContainer).append(html);

    $(this._pillContainer).css('padding-top', '16px');

    /* add the click behaviors */

    $(this._pillContainer).find('.select-pill-wrapper .button').on('click', (e) => {

      /* if they click the close icon, we remove item from selection group */

      const parent  = $(e.target).parent().parent();
      const trainId = parseInt($(parent).data('id'), 10);

      /* find the item */

      const item = this._selection.some(s => s.history._train._generatedId === trainId);

      /* remove it */

      if (item) {

        this._selection = this._selection.filter(s => s.history._train._generatedId !== trainId);

        this.updatePanel();

        this._viewer.setSelection(this.selection());

      }

    });

    $(this._pillContainer).find('.select-pill-wrapper .action').on('click', (e) => {

      /* if they click the close icon, we remove item from selection group */

      const parent = $(e.target).parent();
      const trainId = parseInt($(parent).data('id'), 10);

      /* find the item */

      const items = this._selection.filter(s => s.history._train._generatedId === trainId);

      /* view it */

      if (items.length > 0) {

        this._viewer.focus(items[0].view);

        this._focusIndex = items[0].index;

        this.updatePanel();

        this._viewer.setSelection(this.selection());
      }

    });
  }

  /** @brief add or append to the selection group */
  select(history: MovementHistory, append: boolean): void {

    /**
     *
     * @NOTE append==true means that if the item is already here, then we remove from
     * the selection, if not already here, we append to selection group.
     *
     */

    /* are we clearing the selection ? */

    if (history == null) {

      this._selection = [];

      this._focusIndex = -1;

      this._viewer.setSelection([]);

      this.updatePanel();

      return ;
    }

    /* set */

    if (!append) {

      /* the clicked on a history this is now *the* selection */

      this._selection = [];

      /* get rid of any old version of this one */

      this._selection = this._selection.filter(s => s.history._train._generatedId !== history._train._generatedId);

      /* append */

      this._selection.push({
        view: this._viewer.viewMemento(),
        history,
        index: 0
      });

      /* now tell the viewer to draw the new selection */

      this._focusIndex = -1;

      this._viewer.setSelection(this.selection());


      this.updatePanel();

      return ;
    }

    /*
     * in append mode -- CTRL + click -- if its already in the selection group, we remove it, if not
     * then we add it.
     *
     */

    /* existing position in selection group */

    const has: boolean = this._selection.some(s => s.history._train._generatedId === history._train._generatedId );

    if (has) {

      /* we're removing */

      this._selection = this._selection.filter(s => s.history._train._generatedId !== history._train._generatedId);

      for (let i = 0; i < this._selection.length; i++) {

        this._selection[i].index = i;
      }

    } else {

      /* we're adding */

      this._selection.push({
           view: this._viewer.viewMemento(),
         history,
          index: this._selection.length
      });

    }

    /* now tell the viewer to draw the new selection */

    this._focusIndex = -1;

    this._viewer.setSelection(this.selection());

    this.updatePanel();

    return ;
  }

  /** @brief fetch the current selection group */
  selection(): MovementHistory[] {

    return this._selection.map(s => s.history);
  }

  /** @brief focus on the next selection item */
  focusNext(): void {

    this.show();

    if (this._selection.length === 0) {

      /* nothing to do */

      return ;
    }

    if (this._focusIndex < 0) {
      this._focusIndex = 0;
    }

    this._focusIndex++;

    if (this._focusIndex >= this._selection.length) {

      this._focusIndex = 0;
    }

    this._viewer.setSelection(this.selection());

    this.updatePanel();

    this._viewer.focus(this._selection[this._focusIndex].view);
  }

  /** @brief focus on the previous selection item */
  focusPrevious(): void {

    this.show();

    if (this._selection.length === 0) {

      /* nothing to do */

      return ;
    }

    if (this._focusIndex < 0) {
      this._focusIndex = 0;
    }

    this._focusIndex--;

    if (this._focusIndex < 0) {

      this._focusIndex = this._selection.length - 1;
    }

    this._viewer.setSelection(this.selection());

    this.updatePanel();

    this._viewer.focus(this._selection[this._focusIndex].view);

  }

  /** @brief test to see if this one is the one currently focused on */
  isFocus(history: MovementHistory) {

    if ((this._focusIndex < 0) || (this._selection.length === 0)) {

      /* there is no focus */

      return false;
    }

    return this._selection[this._focusIndex].history._train._generatedId === history._train._generatedId;
  }

  /** @brief show the drop down panel */
  show() {

    $(this._pillContainer).css('display', 'block');
  }

  /** @brief hide the drop down panel */
  hide() {

    $(this._pillContainer).css('display', 'none');
  }

  /** @brief test if the dropdown is visible */
  visible(): boolean {

    return $(this._pillContainer).css('display') !== 'none';
  }
}
